import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ScenarioControllerApi } from '../api/generated';
import { createApiConfiguration } from '../api/apiConfig';
import { useAppDispatch } from '../store/hooks';
import { showError } from '../store/errorDialog/errorDialogSlice';
import type { WeekData, WeekFilterState } from '../types/weekOverview';
import {
  buildUnplannedWeekData,
  mapSchedulingResultToWeekData
} from '../services/schedulingResultService';
import { fetchWeekSchedulingResult } from '../services/weekSchedulingResultService';
import type { WeekResultOutcome } from '../services/weekSchedulingResultService';
import { useLocations } from './useLocations';
import { useStaffCompetences } from './useStaffCompetences';
import {
  addWeeks,
  getCurrentIsoYearAndWeek,
  getIsoYearAndWeekForDate
} from '../utils/weekDateUtils';

export interface UseWeekOverviewResult {
  weekData: WeekData | null;
  isLoading: boolean;
  // The week shell still renders on a failure so the page keeps its navigation,
  // but the body must not then claim the week is unplanned — that would report a
  // failed request as a fact about the plan.
  hasLoadFailed: boolean;
  selectedWeek: number;
  selectedYear: number;
  filters: WeekFilterState;
  setFilters: (filters: WeekFilterState) => void;
  goToPreviousWeek: () => void;
  goToNextWeek: () => void;
  goToToday: () => void;
  goToDate: (date: Date) => void;
  isViewingCurrentWeek: boolean;
}

const EMPTY_FILTERS: WeekFilterState = {
  roomId: null,
  staffId: null,
  patientId: null
};

// The fetch outcome, remembered together with the location and week it was
// fetched for so a stale response never renders under another location's or
// week's header.
interface LoadedResult {
  locationId: string;
  year: number;
  week: number;
  outcome: WeekResultOutcome;
}

export function useWeekOverview(locationId: string | undefined): UseWeekOverviewResult {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { locationsById } = useLocations();
  const { competences } = useStaffCompetences();

  const current = useMemo(() => getCurrentIsoYearAndWeek(), []);
  const [selectedYear, setSelectedYear] = useState(current.year);
  const [selectedWeek, setSelectedWeek] = useState(current.week);
  const [filters, setFilters] = useState<WeekFilterState>(EMPTY_FILTERS);

  const [loadedResult, setLoadedResult] = useState<LoadedResult | null>(null);
  // The most recently settled fetch, as a location|year|week key. Loading is
  // derived by comparing it with the current selection, so the effect never
  // needs a synchronous setState to flip a loading flag.
  const [settledFetchKey, setSettledFetchKey] = useState<string | null>(null);

  // Scenarios are stable within a session, so week navigation inside the same
  // year reuses the resolved scenario instead of refetching it.
  const scenarioIdByYearRef = useRef(new Map<string, string | null>());
  // Monotonic fetch counter: only the newest request may write state, so fast
  // week-navigation can never let an older response overtake a newer one.
  const fetchSeqRef = useRef(0);

  useEffect(() => {
    if (!locationId) return;
    const seq = ++fetchSeqRef.current;
    // Unmounting does not re-run this effect, so the sequence counter alone
    // cannot stop a late failure from raising a dialog over the next page.
    let isActive = true;
    const isCurrent = (): boolean => isActive && fetchSeqRef.current === seq;
    const fetchKey = `${locationId}|${selectedYear}|${selectedWeek}`;

    const config = createApiConfiguration();
    const scenarioCacheKey = `${locationId}|${selectedYear}`;
    const cachedScenarioId = scenarioIdByYearRef.current.get(scenarioCacheKey);

    const resolveScenarioId =
      cachedScenarioId === undefined
        ? new ScenarioControllerApi(config)
            .getScenarioForYear({ locationId, year: selectedYear })
            .then((scenarioResponse) => {
              const fetchedScenarioId = scenarioResponse.data.id ?? null;
              scenarioIdByYearRef.current.set(scenarioCacheKey, fetchedScenarioId);
              return fetchedScenarioId;
            })
        : Promise.resolve(cachedScenarioId);

    resolveScenarioId
      // A year with no scenario has nothing to calculate, so it reads as
      // unplanned; a scenario lookup that fails is a real failure.
      .then((resolvedScenarioId): Promise<WeekResultOutcome> | WeekResultOutcome => {
        // Navigating away while the scenario resolved makes the week result
        // pointless to ask for; the outcome returned here is discarded below.
        if (!isCurrent()) return { kind: 'unplanned' };
        if (!resolvedScenarioId) return { kind: 'unplanned' };
        return fetchWeekSchedulingResult({
          locationId,
          scenarioId: resolvedScenarioId,
          weekNumber: selectedWeek
        });
      })
      .catch((error: unknown): WeekResultOutcome => {
        console.error(`Failed to resolve the scenario for ${scenarioCacheKey}:`, error);
        return { kind: 'failed', status: undefined };
      })
      .then((outcome) => {
        if (!isCurrent()) return;
        setLoadedResult({ locationId, year: selectedYear, week: selectedWeek, outcome });
        setSettledFetchKey(fetchKey);
        if (outcome.kind === 'failed') {
          dispatch(showError({ message: t('fetch-error') }));
        }
      });

    return () => {
      isActive = false;
    };
  }, [locationId, selectedYear, selectedWeek, dispatch, t]);

  const locationName = locationId ? (locationsById[locationId]?.name ?? '') : '';
  const isLoading =
    Boolean(locationId) && settledFetchKey !== `${locationId}|${selectedYear}|${selectedWeek}`;

  const isLoadedForSelection =
    loadedResult !== null &&
    loadedResult.locationId === locationId &&
    loadedResult.year === selectedYear &&
    loadedResult.week === selectedWeek;

  const hasLoadFailed = isLoadedForSelection && loadedResult.outcome.kind === 'failed';

  const weekData = useMemo<WeekData | null>(() => {
    if (!locationId) return null;
    if (isLoadedForSelection && loadedResult.outcome.kind === 'planned') {
      return mapSchedulingResultToWeekData(
        loadedResult.outcome.response,
        selectedYear,
        selectedWeek,
        locationName,
        competences
      );
    }
    // The shell keeps the page's header and week navigation alive while loading,
    // when the week genuinely has no plan, and when the fetch failed.
    return buildUnplannedWeekData(selectedYear, selectedWeek, locationName);
  }, [
    locationId,
    isLoadedForSelection,
    loadedResult,
    selectedYear,
    selectedWeek,
    locationName,
    competences
  ]);

  const goToPreviousWeek = useCallback(() => {
    const next = addWeeks(selectedYear, selectedWeek, -1);
    setSelectedYear(next.year);
    setSelectedWeek(next.week);
  }, [selectedYear, selectedWeek]);

  const goToNextWeek = useCallback(() => {
    const next = addWeeks(selectedYear, selectedWeek, 1);
    setSelectedYear(next.year);
    setSelectedWeek(next.week);
  }, [selectedYear, selectedWeek]);

  const goToToday = useCallback(() => {
    const today = getCurrentIsoYearAndWeek();
    setSelectedYear(today.year);
    setSelectedWeek(today.week);
  }, []);

  const goToDate = useCallback((date: Date) => {
    // Ignore an invalid Date (e.g. a failed date-picker parse): otherwise the
    // NaN year/week would flow into getIsoWeekStartDate and crash toISOString().
    if (Number.isNaN(date.getTime())) return;
    const target = getIsoYearAndWeekForDate(date);
    setSelectedYear(target.year);
    setSelectedWeek(target.week);
  }, []);

  const isViewingCurrentWeek = selectedYear === current.year && selectedWeek === current.week;

  return {
    weekData,
    isLoading,
    hasLoadFailed,
    selectedWeek,
    selectedYear,
    filters,
    setFilters,
    goToPreviousWeek,
    goToNextWeek,
    goToToday,
    goToDate,
    isViewingCurrentWeek
  };
}
